import { WifiOff, RefreshCw } from 'lucide-react';
import { useNetworkStatusContext } from '@/contexts/useNetworkStatusContext';
import { cn } from '@/lib/utils';

interface OfflineBannerProps {
    className?: string;
}

/**
 * Offline Banner Component
 *
 * Displays a persistent banner when the user is offline.
 * Place this at the top of your layout.
 *
 * @example
 * <Layout>
 *   <OfflineBanner />
 *   <Header />
 *   <main>{children}</main>
 * </Layout>
 */
export const OfflineBanner = ({ className }: OfflineBannerProps) => {
    const { isOnline } = useNetworkStatusContext();

    if (isOnline) return null;

    return (
        <div
            className={cn(
                'bg-warning text-warning-foreground px-4 py-2',
                'flex items-center justify-center gap-2 text-sm font-medium',
                className
            )}
            role="alert"
            aria-live="polite"
        >
            <WifiOff className="h-4 w-4" aria-hidden="true" />
            <span>You&apos;re offline. You can keep browsing, but live availability and checkout may not work until you reconnect.</span>
            <button
                onClick={() => window.location.reload()}
                className="ml-2 underline hover:no-underline flex items-center gap-1"
                type="button"
            >
                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                Try again
            </button>
        </div>
    );
};

export default OfflineBanner;
